import { UploadOutlined } from "@ant-design/icons"
import { DragEvent, ReactNode, useRef, useState } from "react"
import styled from "styled-components"

import { COLORS } from "#styles/colors"
import {
  dragHasDroppableMedia,
  getDroppedMediaFiles,
} from "#utils/mediaDropHelpers"

const SWrapper = styled.div`
  position: relative;
  width: 100%;
`

/**
 * Feedback only — pointer-events: none keeps drag events landing on the
 * wrapper, so crossing onto the overlay cannot fire a spurious dragleave.
 */
const SOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  pointer-events: none;
  border: 2px dashed ${COLORS.theme.colorPrimary};
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
  color: ${COLORS.theme.colorPrimary};
  font-weight: 600;
  font-size: 13px;
`

/**
 * Wraps children in an invisible drag-and-drop file target. Renders nothing
 * extra until a droppable file is dragged over it, then shows a "drop to
 * upload" overlay. Accepted media kinds live in mediaDropHelpers (images
 * today; audio has a reserved slot). Pages hosting this should also mount
 * usePreventWindowFileDrop so a missed drop cannot navigate the browser away.
 */
const FileDropTarget = ({
  onDropFiles,
  message = "Drop to upload",
  testId,
  children,
}: {
  /** Receives only the droppable files from the drop; never called empty. */
  onDropFiles: (files: File[]) => void
  message?: string
  testId?: string
  children: ReactNode
}) => {
  const [isDragActive, setIsDragActive] = useState(false)
  // dragenter/dragleave fire for every child crossed; the counter nets them out.
  const depthRef = useRef(0)

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!dragHasDroppableMedia(event.dataTransfer)) return
    event.preventDefault()
    depthRef.current += 1
    setIsDragActive(true)
  }

  const handleDragLeave = () => {
    if (depthRef.current === 0) return
    depthRef.current -= 1
    if (depthRef.current === 0) setIsDragActive(false)
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!dragHasDroppableMedia(event.dataTransfer)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    depthRef.current = 0
    setIsDragActive(false)

    const files = getDroppedMediaFiles(event.dataTransfer)
    // Nothing usable: fall through to the window-level guard, which cancels
    // the browser's default navigate-to-file without uploading anything.
    if (files.length === 0) return

    event.preventDefault()
    event.stopPropagation()
    onDropFiles(files)
  }

  return (
    <SWrapper
      data-testid={testId}
      data-drag-active={isDragActive}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragActive && (
        <SOverlay data-testid="file-drop-overlay">
          <UploadOutlined />
          {message}
        </SOverlay>
      )}
    </SWrapper>
  )
}

export default FileDropTarget
